import React from "react";

import LinearProgress from "@mui/material/LinearProgress";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./MetricCard.module.css";

type MetricCardCommonProps = {
  title: string;
  isLoading?: boolean;
};

type MetricCardSingleValueProps = {
  type: "single-value";
  value: number;
  label: string;
};

type MetricCardTimeframeProps = {
  type: "timeframe";
  values: Array<{
    value: number;
    label: string;
  }>;
};

type MetricCardChartProps = {
  type: "chart";
  currentValue: number;
  totalValue: number;
};

type MetricCardProps = MetricCardCommonProps &
  (
    | MetricCardSingleValueProps
    | MetricCardTimeframeProps
    | MetricCardChartProps
  );

// TODO: chart type - values validation?

const MetricCard: React.FC<MetricCardProps> = (props) => {
  if (props.type === "single-value") {
    const { isLoading, title, value, label } = props;

    return (
      <div className={toClassName(styles.container, styles.containerSpacer)}>
        {isLoading ? (
          <Skeleton variant="rounded" width="25%" />
        ) : (
          <Text variant="body" color="textSubtle">
            {title}
          </Text>
        )}

        <div className={styles.singleValueMetadata}>
          {isLoading ? (
            <Skeleton variant="rounded" height={24} width="40%" />
          ) : (
            <>
              <Text variant="title2">{value}</Text>
              <Text variant="bodySmall" color="textSubtle">
                {label}
              </Text>
            </>
          )}
        </div>
      </div>
    );
  }

  if (props.type === "timeframe") {
    const { isLoading, title, values } = props;

    return (
      <div className={toClassName(styles.container, styles.containerSpacer)}>
        {isLoading ? (
          <Skeleton variant="rounded" width="65%" />
        ) : (
          <Text variant="body" color="textSubtle">
            {title}
          </Text>
        )}

        <div className={styles.timeframeMetadataContainer}>
          {values.map(({ value, label }) => (
            <div key={label} className={styles.timeframeMetadata}>
              {isLoading ? (
                <Skeleton variant="rounded" height={40} />
              ) : (
                <>
                  <Text variant="title2">{value}</Text>
                  <Text variant="bodySmall" color="textSubtle">
                    {label}
                  </Text>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const { isLoading, title, currentValue, totalValue } = props;
  const progressPercentage = (currentValue / totalValue) * 100;

  return (
    <div className={styles.container}>
      {isLoading ? (
        <Skeleton variant="rounded" width="70%" />
      ) : (
        <Text variant="body" color="textSubtle">
          {title}
        </Text>
      )}

      <div className={styles.chartMetadataContainer}>
        {isLoading ? (
          <Skeleton variant="rounded" height={24} width="6%" />
        ) : (
          <Text variant="title2">{currentValue}</Text>
        )}

        <div className={styles.chartMetadata}>
          {isLoading ? (
            <Skeleton variant="rounded" height={12} />
          ) : (
            <LinearProgress
              classes={{ root: styles.chartBarRoot, bar: styles.chartBar }}
              variant="determinate"
              value={progressPercentage}
            />
          )}

          <div className={styles.chartValues}>
            {isLoading ? (
              <Skeleton variant="rounded" height={12} width="8%" />
            ) : (
              <Text variant="bodySmallSolid" color="textSubtle">
                {Math.round(progressPercentage)}%
              </Text>
            )}

            {isLoading ? (
              <Skeleton variant="rounded" height={12} width="25%" />
            ) : (
              <Text variant="bodySmallSolid" color="textSubtle">
                {props.currentValue} of {props.totalValue} total
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricCard;
